import React from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, BookOpenIcon, MessageCircleIcon, HandshakeIcon, CheckCircle2Icon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const coverage = [{
  icon: EyeIcon,
  title: 'Recognizing early signs',
  body: 'Subtle forms of discrimination can take — and how to listen to your child without making them feel alarmed.',
  bg: 'bg-sage-100'
}, {
  icon: BookOpenIcon,
  title: 'Basic rights & responsibilities',
  body: 'A plain-language overview of what schools and institutions are obligated to provide, and what you can expect.',
  bg: 'bg-[#EDF2F7]'
}, {
  icon: MessageCircleIcon,
  title: 'Communicating with institutions',
  body: 'How to raise concerns calmly, document situations, and escalate respectfully when needed.',
  bg: 'bg-[#F7EDE9]'
}, {
  icon: HandshakeIcon,
  title: 'Resolution, not confrontation',
  body: 'The goal is restoring dignity and protecting your child — not winning a fight. Calm engagement is the strongest position.',
  bg: 'bg-sand-100'
}];
const steps = [{
  step: '01',
  title: 'Observe',
  body: 'Listen calmly to your child. Note what happened, when, and who was involved. Stay grounded.'
}, {
  step: '02',
  title: 'Document',
  body: 'Write down details clearly and factually. Photos, messages, names, dates. Avoid emotional language.'
}, {
  step: '03',
  title: 'Engage',
  body: 'Speak with the school or institution directly. Bring documentation. Stay calm and solution-focused.'
}, {
  step: '04',
  title: 'Escalate',
  body: 'If needed, escalate respectfully — to higher staff, governing bodies, or formal complaint channels.'
}];
export function PreventingDiscrimination() {
  return <>
      <SubpageHero eyebrow="Protection, Awareness & Calm Response" title="Preventing Discrimination" paragraphs={['Discrimination is often subtle — and difficult for children to explain. This program helps parents recognize early signs, understand basic rights and school responsibilities, and communicate effectively with institutions.', 'The focus is on prevention, dignity, and resolution — not confrontation.']} primaryCta={{
      label: 'Explore the Program',
      to: '#content'
    }} secondaryCta={{
      label: 'Parenting in Non-Muslim Countries',
      to: '/en/parents/parenting-in-non-muslim-countries'
    }} image="https://cdn.magicpatterns.com/uploads/jHGadue8Tm8CvPGACZTXbP/Discrimination5.jpg" imageAlt="Muslim mother and daughter standing confidently in a busy school hallway" compact masked data-id="element-4439" data-mp="2iplqeh87dtsz" />

      {/* What this program covers */}
      <section id="content" className="py-16 md:py-24 bg-sage-50/60 scroll-mt-24" data-id="element-4440" data-mp="1dm9qgov0ai5k">
        <Container data-id="element-4441" data-mp="18nf1f56g3xd0">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4442" data-mp="13pwpr711n41b">
            What this program covers
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4443" data-mp="yqaeh34rzxv5">
            Practical, calm, and structured guidance for the situations most
            Muslim parents will eventually face.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-4444" data-mp="py02phe8fkbs">
            {coverage.map((c, i) => <motion.div key={c.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl ${c.bg} border border-ink-900/5 p-6 flex gap-4`} data-id="element-4445" data-mp="l06uyjxa6i9e" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700 flex-shrink-0" data-id="element-4446" data-mp="mxgk8oqm3bi1">
                  <c.icon size={18} strokeWidth={1.6} data-id="element-4447" data-mp="cziz4a41tutn" />
                </span>
                <div data-id="element-4448" data-mp="hz5f2rb9hp65">
                  <h3 className="font-display text-lg text-ink-900" data-id="element-4449" data-mp="hzon9gb40z6z">
                    {c.title}
                  </h3>
                  <p className="mt-1 text-ink-700 text-sm leading-relaxed" data-id="element-4450" data-mp="40p7ky5krumy">
                    {c.body}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Calm response framework */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-4451" data-mp="22fick3epa6f3">
        <PatternDecor side="left" y="top" size="lg" data-id="element-4452" data-mp="3mwrj3n3j71wy" />
        <Container data-id="element-4453" data-mp="3mxau0jurkq1h">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4454" data-mp="3muokwfs862h5">
            A calm-response framework
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4455" data-mp="hq128pke68j">
            A simple, repeatable approach you can use whenever a situation
            arises — built for clarity, dignity, and effective resolution.
          </p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-4456" data-mp="ae2ud5h76i2r">
            {steps.map((s, i) => <motion.div key={s.step} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className="rounded-2xl bg-white border border-sage-200 p-6 shadow-soft" data-id="element-4457" data-mp="5g9supquac8t" data-mpi={i}>
                <span className="font-display text-3xl text-sand-400" data-id="element-4458" data-mp="p4sw0yv9tcef">
                  {s.step}
                </span>
                <h3 className="mt-3 font-display text-lg text-ink-900" data-id="element-4459" data-mp="u6046z1bsdlm">
                  {s.title}
                </h3>
                <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-4460" data-mp="134lgicg7ewdy">
                  {s.body}
                </p>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Principles */}
      <section className="py-16 md:py-24 bg-[#EDF2F7]/60" data-id="element-4461" data-mp="1cvg69qsnse45">
        <Container data-id="element-4462" data-mp="1hsyibq1z39gd">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-4463" data-mp="1hthpqctzyfg5">
            <div className="lg:col-span-6" data-id="element-4464" data-mp="1lutj8tfzrktm">
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-4465" data-mp="1lw4jija8aafz">
                Our core principles
              </h2>
              <ul className="mt-6 space-y-3 text-ink-700" data-id="element-4466" data-mp="3bbxeca14kttd">
                {['Prevention first — early awareness reduces harm', "Protect your child's dignity above all", 'Engage institutions calmly, not emotionally', 'Document carefully and factually', 'Resolution is the goal — not confrontation'].map(p => <li key={p} className="flex items-start gap-3" data-id="element-4467" data-mp="3u2y90lv7q374">
                    <CheckCircle2Icon size={18} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.6} data-id="element-4468" data-mp="3u49fitg1lp1x" />
                    <span data-id="element-4469" data-mp="3l7s37mvxzl4l">{p}</span>
                  </li>)}
              </ul>
            </div>
            <div className="lg:col-span-6" data-id="element-4470" data-mp="hl1h1ie7pn4s">
              <div className="rounded-2xl bg-white border border-sage-200 p-7 md:p-8 shadow-soft" data-id="element-4471" data-mp="hltekyrdduxp">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-4472" data-mp="ctjguwk0jqkv">
                  Important note
                </p>
                <p className="text-ink-700 leading-relaxed" data-id="element-4473" data-mp="3m2kvtlzwl20f">
                  This program provides education, awareness, and practical
                  guidance. It is not legal representation. For specific legal
                  matters, we can help you understand the situation and connect
                  with appropriate legal services.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-4474" data-mp="8u89iwd4lzp4">
        <PatternDecor side="right" y="middle" size="md" data-id="element-4475" data-mp="3r1o2favj7gai" />
        <Container data-id="element-4476" data-mp="3v2rf5oia8w7i">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-4477" data-mp="3v27zyfk0olx6">
            <div className="max-w-xl" data-id="element-4478" data-mp="2flzdwf2mq97l">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4479" data-mp="2fp53dvf67d3u">
                Be prepared, not reactive
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-4480" data-mp="2c3tkr9g328ka">
                Membership gives you ongoing access to expert guidance and a
                calm support system.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-4481" data-mp="2m1zkplibx4mi">
              <Link to="/en/waitlist" data-id="element-4482" data-mp="2m0od1alrjcq4">
                <Button variant="primary" data-id="element-4483" data-mp="2h3o66xh3a5xy">Join Membership</Button>
              </Link>
              <Link to="/en/parents/resources-guidance" data-id="element-4484" data-mp="1tdtysvfjh9l3">
                <Button variant="secondary" data-id="element-4485" data-mp="1tc9495t85qn8">View Resources</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}